import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import { ADMIN_TOKEN_STORAGE_KEY, adminApi } from "./axios"
import { DEMO_STAFF_PROFILE, DEMO_TOKEN, isAdminDemoMode } from "./mockData"

export const adminLogin = createAsyncThunk(
  "admin/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data } = await adminApi.login(email, password)
      return {
        token: data.token,
        profile: {
          id: data.id,
          email: data.email,
          name: data.name,
          is_staff: data.is_staff,
          is_superuser: data.is_superuser,
          can_approve_inbox_templates: Boolean(
            data.can_approve_inbox_templates
          ),
          features: data.features || {},
          can_manage_features: Boolean(data.can_manage_features),
        },
      }
    } catch (error) {
      const message =
        error?.response?.data?.email ||
        error?.response?.data?.detail ||
        "Login failed."
      return rejectWithValue(
        Array.isArray(message) ? message.join(" ") : String(message)
      )
    }
  }
)

export const adminRestoreSession = createAsyncThunk(
  "admin/restoreSession",
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY)
    if (!token) {
      return rejectWithValue("no_token")
    }
    if (isAdminDemoMode() && token === DEMO_TOKEN) {
      return { token, profile: DEMO_STAFF_PROFILE }
    }
    try {
      const { data } = await adminApi.fetchProfile()
      return {
        token,
        profile: {
          id: data.id,
          email: data.email,
          name: data.name,
          is_staff: data.is_staff,
          is_superuser: data.is_superuser,
          can_approve_inbox_templates: Boolean(
            data.can_approve_inbox_templates
          ),
          features: data.features || {},
          can_manage_features: Boolean(data.can_manage_features),
        },
      }
    } catch (error) {
      localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY)
      return rejectWithValue("invalid_token")
    }
  }
)

export const adminLogout = createAsyncThunk("admin/logout", async () => {
  await adminApi.logout()
})

const initialToken = localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY)

const adminSlice = createSlice({
  name: "admin",
  initialState: {
    token: initialToken,
    profile:
      initialToken && isAdminDemoMode() && initialToken === DEMO_TOKEN
        ? DEMO_STAFF_PROFILE
        : null,
    loginError: null,
    isLoggingIn: false,
    isRestoringSession: Boolean(initialToken) && !isAdminDemoMode(),
  },
  reducers: {
    setAdminFeatures(state, action) {
      if (state.profile) state.profile.features = action.payload
    },
    clearAdminLoginError(state) {
      state.loginError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(adminLogin.pending, (state) => {
        state.isLoggingIn = true
        state.loginError = null
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.isLoggingIn = false
        state.isRestoringSession = false
        state.token = action.payload.token
        state.profile = action.payload.profile
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.isLoggingIn = false
        state.loginError = action.payload || "Login failed."
        state.token = null
        state.profile = null
      })
      .addCase(adminRestoreSession.pending, (state) => {
        state.isRestoringSession = true
      })
      .addCase(adminRestoreSession.fulfilled, (state, action) => {
        state.isRestoringSession = false
        state.token = action.payload.token
        state.profile = action.payload.profile
      })
      .addCase(adminRestoreSession.rejected, (state) => {
        state.isRestoringSession = false
        state.token = null
        state.profile = null
      })
      .addCase(adminLogout.fulfilled, (state) => {
        state.token = null
        state.profile = null
        state.loginError = null
        state.isRestoringSession = false
      })
  },
})

export const { clearAdminLoginError, setAdminFeatures } = adminSlice.actions
export const selectAdminToken = (state) => state.admin.token
export const selectAdminProfile = (state) => state.admin.profile
export const selectAdminLoginError = (state) => state.admin.loginError
export const selectAdminIsLoggingIn = (state) => state.admin.isLoggingIn
export const selectAdminIsRestoringSession = (state) =>
  state.admin.isRestoringSession

export default adminSlice.reducer
export const selectAdminFeatures = (state) =>
  state.admin.profile?.features || {}
